import phoneUtil from 'google-libphonenumber';

import { ResponseTypes } from 'bundles/survey-form/constants/SurveyFormConstants';

/**
 * EOI (Expression of Interest) phone number type
 */
export type PhoneDetails = { phoneNumber?: string; countryCode?: string; extension?: string };

export const getFullPhoneNumber = (phone: PhoneDetails) => {
  if (phone?.phoneNumber?.startsWith('+')) {
    return phone?.phoneNumber;
  }

  const countryCode = phone?.countryCode || '';
  const phoneNumber = phone?.phoneNumber || '';
  const extension = phone?.extension || '';
  const fullPhoneNumber = '+' + countryCode + phoneNumber + extension;

  return fullPhoneNumber;
};

/**
 * This function transforms a given phone details object into the structure required by the application.
 *
 * @param {PhoneDetails} phone - The phone details to be formatted.
 * @returns {Object} The formatted phone input structure.
 */
export const getPhoneNumberInput = (phone: PhoneDetails) => {
  return {
    phoneNumber: {
      [ResponseTypes.SHORT_ANSWER_RESPONSE]: {
        text: phone,
      },
    },
  };
};

/**
 * This function attempts to parse a phone number using the specified region code (default is 'US').
 * If the phone number is valid, it returns the parsed number details. Otherwise, it returns undefined.
 *
 * @param {string} phoneNumber - The phone number to be parsed.
 * @param {string} [regionCode='US'] - The region code to be used for parsing the phone number. Defaults to 'US'.
 * @returns {Object|undefined} The parsed phone number details if the number is valid, otherwise undefined.
 */
export const getParsedNumber = (phoneNumber: string, regionCode = 'US') => {
  try {
    const phoneParser = phoneUtil.PhoneNumberUtil.getInstance();
    const parsedNumber = phoneParser.parseAndKeepRawInput(phoneNumber, regionCode);
    const isPossibleNumber = phoneParser.isPossibleNumber(parsedNumber);

    if (!isPossibleNumber) {
      return undefined;
    }

    return parsedNumber;
  } catch {
    // If phone input cannot be parsed because invalid format, return undefined
    return undefined;
  }
};

export const getPhoneDetails = (phoneNumber: string): undefined | { phone: PhoneDetails } => {
  const parsedNumber = getParsedNumber(phoneNumber);

  if (!parsedNumber) return parsedNumber;

  const nationalNumber = parsedNumber.getNationalNumber();
  const countryCode = parsedNumber.getCountryCode();
  const extension = parsedNumber.getExtension();

  const phone = {
    phoneNumber: nationalNumber ? nationalNumber.toString() : '',
    countryCode: countryCode ? countryCode.toString() : '',
    extension: extension ? extension.toString() : '',
  };

  return { phone };
};

/**
 * This function attempts to get the phone details for the given phone number. If the phone number is not recognized with its current format,
 * it retries by prefixing the phone number with a '+' character. If no details are found, it returns the phone number in a default format.
 *
 * @param {string} phoneNumber - The phone number to be formatted.
 * @returns {Object} The formatted phone number details.
 */
export const getPhoneNumberFormat = (phoneNumber: string) => {
  const phoneDetails = getPhoneDetails(phoneNumber) || getPhoneDetails('+' + phoneNumber);
  const defaultPhoneNumberFormat = { phone: { phoneNumber } };

  const phoneNumberFormat = phoneDetails || defaultPhoneNumberFormat;

  return phoneNumberFormat;
};

export default { getFullPhoneNumber, getPhoneNumberInput, getParsedNumber, getPhoneDetails, getPhoneNumberFormat };
